import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { OtpStep } from "@/components/OtpStep";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const r = await login(email, password);
      if (r?.mfa_required) setMfa(r);
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden flex-col justify-between overflow-hidden bg-slate-900 p-12 text-white lg:flex">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "linear-gradient(#fff 1px,transparent 1px),linear-gradient(90deg,#fff 1px,transparent 1px)", backgroundSize: "48px 48px" }} />
        <div className="relative flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-lg bg-indigo-600 font-display text-xl font-bold">A</div>
          <span className="font-display text-2xl font-semibold">Aspire</span>
        </div>
        <div className="relative max-w-lg">
          <div className="overline !text-indigo-300 mb-4">Project governance, enforced by workflow</div>
          <h1 className="font-display text-5xl font-semibold leading-tight">Run a fully governed project without reading a manual.</h1>
          <div className="mt-10 flex gap-2">
            {["G0", "G1", "G2", "G3", "G4"].map((g, i) => (
              <div key={g} className="flex-1 rounded-md border border-slate-700 bg-slate-800/60 px-3 py-2 fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                <div className="font-mono text-xs text-indigo-300">{g}</div>
                <div className="text-xs text-slate-300">{["Intake", "Concept", "Baseline", "Checkpoint", "Closure"][i]}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-slate-500">ASPIRE Project & Operational Governance Frameworks v1.0</div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-6">
          <div>
            <h2 className="font-display text-3xl font-semibold">Sign in</h2>
            <p className="mt-1 text-sm text-slate-500">Your home screen opens with your pending actions first.</p>
          </div>
          {mfa ? <OtpStep mfa={mfa} onCancel={() => { setMfa(null); setPassword(""); }} /> : (
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
            <div className="space-y-1.5"><div className="flex items-center justify-between"><Label>Password</Label><Link to="/forgot-password" className="text-xs font-medium text-indigo-600 hover:underline" data-testid="forgot-password-link">Forgot password?</Link></div><Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required /></div>
            {err && <div data-testid="login-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
            <Button data-testid="login-submit" type="submit" className="w-full" disabled={busy}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Sign in</Button>
          </form>)}
        </div>
      </div>
    </div>
  );
}
